import { inject, Injectable, InjectionToken } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { environment } from '@telespot/shared/environment';
import { LoggerService } from '@telespot/shared/logger/feature/util';

import { Recorder } from '../../models/recorder';

export const RECORDER_TOKEN = new InjectionToken<Recorder>('Recorder');

/**
 * Whether this viewer's refusal actually stops the recording. Left unprovided, every refusal is
 * honored; the app narrows it down to the roles whose refusal counts.
 */
export const HONOR_DENIAL = new InjectionToken<Observable<boolean>>('HonorRecordingDenial');

export const RECORDING_CONSENT_KEY = 'recording_identification_enabled';

@Injectable({
  providedIn: 'root',
})
export class RecorderService {
  private readonly logger = inject(LoggerService, { optional: true });
  private readonly recorder = inject(RECORDER_TOKEN, { optional: true });
  private readonly honorDenial$ = inject(HONOR_DENIAL, { optional: true }) ?? of(true);
  private readonly _consent$ = new BehaviorSubject<boolean>(this.consented);

  /** The current choice, then every change — consent can be granted long after sign-in. */
  public readonly consent$: Observable<boolean> = this._consent$.asObservable();

  constructor() {
    this.logger?.debug(
      `Recorder service is ${environment.recorder?.enable ? 'ENABLED' : 'DISABLED'}, ` +
        `stored consent: ${this.consented ? 'ALLOW' : 'DENY'}`
    );
    this.watchRecordingState();
  }

  public setConsent(allow: boolean): void {
    this.consented = allow;
    this.logger?.debug(`Setting consent to ${this.consented ? 'ALLOW' : 'DENY'}`);
    this._consent$.next(this.consented);
  }

  public get hasConcent(): boolean {
    return this.consented;
  }

  public identify(id: string, data: { [key: string]: unknown }): void {
    const isSpotlabMember = /@spotlab.(org|ai)/i.test(data?.email as string);

    if (!this.consented) {
      this.logger?.warn(`RecorderService.identify() failed: consent has been denied`);
      return;
    }

    if (environment.recorder?.disable_spotlab_users && data?.email && isSpotlabMember) {
      this.logger?.warn(`Recorder recording paused for Spotlab users`);
      this.recorder?.disable();
      return;
    }

    this.recorder?.identify(id, data);
  }

  /**
   * Recording runs while consent stands, and also for a viewer whose refusal is not honored.
   * `consented` rides along so a grant arriving mid-recording re-declares it to the recorder.
   */
  private watchRecordingState(): void {
    combineLatest([this._consent$, this.honorDenial$])
      .pipe(
        map(([consented, honorDenial]) => ({ record: consented || !honorDenial, consented })),
        distinctUntilChanged(
          (previous, current) => previous.record === current.record && previous.consented === current.consented
        ),
        takeUntilDestroyed()
      )
      .subscribe(({ record, consented }) => {
        if (record) this.recorder?.start(consented);
        else this.recorder?.stop();
      });
  }

  /** An unset choice reads as a denial. */
  private get consented(): boolean {
    return localStorage.getItem(RECORDING_CONSENT_KEY) === 'true';
  }

  private set consented(value: boolean) {
    localStorage.setItem(RECORDING_CONSENT_KEY, value.toString());
  }
}
